import React, { useState, useEffect } from 'react';
import { getReviews, submitReview } from '../services/ReviewService';

const StarDisplay = ({ rating, size = 16 }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((s) => (
            <svg key={s} width={size} height={size} viewBox="0 0 24 24" fill={s <= rating ? '#f59e0b' : '#d1d5db'} style={{ display: 'block', flexShrink: 0 }}>
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
        ))}
    </span>
);

const StarSelector = ({ value, hoverValue, onHover, onLeave, onSelect, size = 34 }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((s) => (
            <svg
                key={s}
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill={s <= (hoverValue || value) ? '#f59e0b' : '#d1d5db'}
                style={{ cursor: 'pointer', transition: 'transform 0.15s, fill 0.15s', transform: s <= (hoverValue || value) ? 'scale(1.15)' : 'scale(1)', display: 'block', flexShrink: 0 }}
                onMouseEnter={() => onHover(s)}
                onMouseLeave={onLeave}
                onClick={() => onSelect(s)}
            >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
        ))}
    </span>
);

const ReviewSection = ({ targetType = 'service', targetId = '', targetTitle = '' }) => {
    const [summary, setSummary] = useState({
        averageRating: 0,
        totalReviews: 0,
        ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        reviews: []
    });
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    const [rating, setRating] = useState(5);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);

    const loadReviews = async () => {
        if (!targetId) { setLoading(false); return; }
        setLoading(true);
        try {
            const data = await getReviews(targetType, targetId);
            if (data) {
                setSummary({
                    averageRating: data.averageRating || 0,
                    totalReviews: data.totalReviews || 0,
                    ratingCounts: data.ratingCounts || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
                    reviews: data.reviews || []
                });
            }
        } catch (err) {
            console.error('Error loading reviews:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadReviews(); }, [targetType, targetId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMsg(null);
        setErrorMsg(null);
        if (!fullName.trim()) { setErrorMsg('Vui lòng nhập Họ và tên!'); return; }
        if (!phone.trim()) { setErrorMsg('Vui lòng nhập Số điện thoại!'); return; }
        if (!comment.trim()) { setErrorMsg('Vui lòng nhập nhận xét!'); return; }

        setSubmitting(true);
        try {
            const res = await submitReview({ targetType, targetId, targetTitle, rating, comment, fullName, phone });
            setSuccessMsg(res?.message || 'Đánh giá đã được gửi thành công! Đang chờ quản trị viên phê duyệt.');
            setComment(''); setFullName(''); setPhone(''); setRating(5);
            setTimeout(() => { setShowModal(false); setSuccessMsg(null); }, 2800);
        } catch (err) {
            setErrorMsg('Gửi đánh giá thất bại. Vui lòng thử lại sau!');
        } finally {
            setSubmitting(false);
        }
    };

    const maxCount = Math.max(...Object.values(summary.ratingCounts), 1);

    return (
        <div style={{ marginTop: '40px' }}>
            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                <div style={{ width: '4px', height: '28px', backgroundColor: '#dc2626', borderRadius: '2px', flexShrink: 0 }} />
                <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Đánh giá &amp; Nhận xét dịch vụ
                </h3>
            </div>

            {/* Summary box */}
            <div style={{
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '28px',
                marginBottom: '28px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
            }}>
                <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Left: Average score */}
                    <div style={{ textAlign: 'center', minWidth: '130px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>
                            Đánh Giá TB
                        </div>
                        <div style={{ fontSize: '52px', fontWeight: '900', color: '#dc2626', lineHeight: '1', marginBottom: '6px' }}>
                            {summary.totalReviews > 0 ? summary.averageRating.toFixed(1) : '—'}
                        </div>
                        <StarDisplay rating={Math.round(summary.averageRating)} size={18} />
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginTop: '6px' }}>
                            {summary.totalReviews > 0 ? `${summary.totalReviews} lượt đánh giá` : 'Chưa có đánh giá'}
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ width: '1px', backgroundColor: '#e2e8f0', alignSelf: 'stretch', flexShrink: 0 }} />

                    {/* Middle: Star bars */}
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        {[5, 4, 3, 2, 1].map((num) => {
                            const count = summary.ratingCounts[num] || 0;
                            const percent = maxCount > 0 ? (count / maxCount) * 100 : 0;
                            return (
                                <div key={num} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    marginBottom: '8px'
                                }}>
                                    {/* Label: number + star inline */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        width: '36px',
                                        flexShrink: 0
                                    }}>
                                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#475569', lineHeight: 1 }}>{num}</span>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="#f59e0b" style={{ display: 'block', flexShrink: 0 }}>
                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                        </svg>
                                    </div>

                                    {/* Progress bar */}
                                    <div style={{
                                        flex: 1,
                                        height: '8px',
                                        backgroundColor: '#f1f5f9',
                                        borderRadius: '99px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: `${percent}%`,
                                            height: '100%',
                                            backgroundColor: percent > 0 ? '#f59e0b' : '#e2e8f0',
                                            borderRadius: '99px',
                                            transition: 'width 0.5s ease'
                                        }} />
                                    </div>

                                    {/* Count */}
                                    <span style={{
                                        width: '24px',
                                        textAlign: 'right',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        color: count > 0 ? '#475569' : '#cbd5e1',
                                        flexShrink: 0
                                    }}>
                                        {count}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Right: CTA */}
                    <div style={{ textAlign: 'center', minWidth: '200px' }}>
                        <div style={{
                            width: '56px', height: '56px',
                            backgroundColor: '#fef2f2',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 12px'
                        }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '12px', lineHeight: '1.4' }}>
                            Bạn đã trải nghiệm<br />dịch vụ này chưa?
                        </div>
                        <button
                            onClick={() => setShowModal(true)}
                            style={{
                                backgroundColor: '#dc2626',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '99px',
                                padding: '11px 22px',
                                fontSize: '13px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                letterSpacing: '0.3px',
                                boxShadow: '0 4px 14px rgba(220,38,38,0.3)',
                                transition: 'all 0.2s',
                                textTransform: 'uppercase'
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                            ✏ Gửi đánh giá
                        </button>
                    </div>
                </div>
            </div>

            {/* Review list */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                    <div style={{ fontSize: '14px' }}>Đang tải đánh giá...</div>
                </div>
            ) : summary.reviews.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {summary.reviews.map((rev, idx) => {
                        const initial = rev.fullName ? rev.fullName.trim().charAt(0).toUpperCase() : '?';
                        const colors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];
                        const color = colors[initial.charCodeAt(0) % colors.length];
                        return (
                            <div key={rev._id || idx} style={{
                                padding: '20px 0',
                                borderBottom: idx < summary.reviews.length - 1 ? '1px solid #f1f5f9' : 'none',
                                display: 'flex',
                                gap: '14px',
                                alignItems: 'flex-start'
                            }}>
                                {/* Avatar */}
                                <div style={{
                                    width: '44px', height: '44px',
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                    color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: '800', fontSize: '17px',
                                    flexShrink: 0
                                }}>
                                    {initial}
                                </div>

                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                                        <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{rev.fullName}</span>
                                        <span style={{
                                            fontSize: '11px', color: '#10b981',
                                            backgroundColor: '#ecfdf5',
                                            padding: '2px 8px', borderRadius: '99px',
                                            fontWeight: '600', border: '1px solid #a7f3d0',
                                            display: 'inline-flex', alignItems: 'center', gap: '3px'
                                        }}>
                                            ✓ Đã trải nghiệm tại Garage
                                        </span>
                                    </div>

                                    {/* Stars + score inline */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                                        <StarDisplay rating={rev.rating} size={14} />
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#f59e0b' }}>
                                            {rev.rating}/5
                                        </span>
                                    </div>

                                    <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.65', margin: '0 0 6px' }}>
                                        {rev.comment}
                                    </p>
                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                        {rev.createdAt ? new Date(rev.createdAt).toLocaleDateString('vi-VN') : ''}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{
                    textAlign: 'center', padding: '40px 20px',
                    backgroundColor: '#f8fafc', borderRadius: '12px',
                    border: '1px dashed #e2e8f0', color: '#94a3b8'
                }}>
                    <div style={{ fontSize: '36px', marginBottom: '10px' }}>💬</div>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                        Chưa có đánh giá nào. Hãy là người đầu tiên chia sẻ cảm nhận!
                    </p>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div style={{
                    position: 'fixed', inset: 0,
                    backgroundColor: 'rgba(15,23,42,0.65)',
                    zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '16px',
                    backdropFilter: 'blur(2px)'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '20px',
                        maxWidth: '520px', width: '100%',
                        maxHeight: '92vh', overflowY: 'auto',
                        padding: '32px',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
                        position: 'relative'
                    }}>
                        <button onClick={() => setShowModal(false)} style={{
                            position: 'absolute', top: '16px', right: '16px',
                            border: 'none', background: '#f1f5f9',
                            borderRadius: '50%', width: '32px', height: '32px',
                            fontSize: '18px', color: '#64748b', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>×</button>

                        {/* Modal header */}
                        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                            <div style={{
                                width: '52px', height: '52px',
                                backgroundColor: '#fef2f2', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 12px'
                            }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                            </div>
                            <h4 style={{ fontWeight: '800', color: '#1e293b', fontSize: '20px', margin: '0 0 4px' }}>
                                GỬI ĐÁNH GIÁ DỊCH VỤ
                            </h4>
                            <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
                                Chia sẻ trải nghiệm của bạn để giúp mọi người
                            </p>
                        </div>

                        {successMsg && (
                            <div style={{
                                backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0',
                                color: '#065f46', padding: '12px 16px', borderRadius: '10px',
                                fontSize: '13px', fontWeight: '600', marginBottom: '16px',
                                display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                                <span>✓</span> {successMsg}
                            </div>
                        )}
                        {errorMsg && (
                            <div style={{
                                backgroundColor: '#fef2f2', border: '1px solid #fca5a5',
                                color: '#991b1b', padding: '12px 16px', borderRadius: '10px',
                                fontSize: '13px', fontWeight: '600', marginBottom: '16px',
                                display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                                <span>⚠</span> {errorMsg}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            {/* Star selector */}
                            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                <label style={{ display: 'block', fontWeight: '700', fontSize: '14px', color: '#334155', marginBottom: '12px' }}>
                                    Bạn đánh giá dịch vụ này bao nhiêu sao?
                                </label>
                                <StarSelector
                                    value={rating}
                                    hoverValue={hoverRating}
                                    onHover={setHoverRating}
                                    onLeave={() => setHoverRating(0)}
                                    onSelect={setRating}
                                    size={38}
                                />
                                <div style={{ marginTop: '6px', fontSize: '13px', color: '#f59e0b', fontWeight: '700' }}>
                                    {['', 'Rất tệ', 'Tệ', 'Bình thường', 'Tốt', 'Xuất sắc'][hoverRating || rating]}
                                </div>
                            </div>

                            {/* Comment */}
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>
                                    Nhận xét của bạn <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <textarea
                                    rows={4}
                                    placeholder="Chia sẻ cảm nhận của bạn về dịch vụ..."
                                    value={comment}
                                    onChange={e => setComment(e.target.value)}
                                    style={{
                                        width: '100%', borderRadius: '10px',
                                        border: '1.5px solid #e2e8f0', padding: '10px 14px',
                                        fontSize: '14px', resize: 'vertical',
                                        outline: 'none', boxSizing: 'border-box',
                                        fontFamily: 'inherit', color: '#334155',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={e => e.target.style.borderColor = '#dc2626'}
                                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                />
                            </div>

                            {/* Name + Phone */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>
                                        Họ và tên <span style={{ color: '#dc2626' }}>*</span>
                                    </label>
                                    <input
                                        type="text" placeholder="Họ và tên"
                                        value={fullName} onChange={e => setFullName(e.target.value)}
                                        style={{
                                            width: '100%', borderRadius: '10px',
                                            border: '1.5px solid #e2e8f0', padding: '10px 14px',
                                            fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                                            fontFamily: 'inherit', color: '#334155'
                                        }}
                                        onFocus={e => e.target.style.borderColor = '#dc2626'}
                                        onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', color: '#475569', marginBottom: '6px' }}>
                                        Số điện thoại <span style={{ color: '#dc2626' }}>*</span>
                                    </label>
                                    <input
                                        type="text" placeholder="0987..."
                                        value={phone} onChange={e => setPhone(e.target.value)}
                                        style={{
                                            width: '100%', borderRadius: '10px',
                                            border: '1.5px solid #e2e8f0', padding: '10px 14px',
                                            fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                                            fontFamily: 'inherit', color: '#334155'
                                        }}
                                        onFocus={e => e.target.style.borderColor = '#dc2626'}
                                        onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                                    />
                                </div>
                            </div>

                            {/* Buttons */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" onClick={() => setShowModal(false)} style={{
                                    padding: '10px 20px', borderRadius: '10px',
                                    border: '1.5px solid #e2e8f0', backgroundColor: '#f8fafc',
                                    color: '#64748b', fontWeight: '600', fontSize: '14px', cursor: 'pointer'
                                }}>
                                    Đóng
                                </button>
                                <button type="submit" disabled={submitting} style={{
                                    padding: '10px 24px', borderRadius: '10px',
                                    border: 'none', backgroundColor: submitting ? '#fca5a5' : '#dc2626',
                                    color: '#fff', fontWeight: '700', fontSize: '14px',
                                    cursor: submitting ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 4px 12px rgba(220,38,38,0.25)',
                                    transition: 'all 0.2s'
                                }}>
                                    {submitting ? 'Đang gửi...' : '✓ Gửi đánh giá'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReviewSection;
